/**
 * useFeaturedItems - React hook for fetching featured items from Voyager API
 *
 * Provides loading state, error handling, and data transformation
 * for the Featured Block component.
 */
import { useState, useEffect, useCallback } from 'react';
import type { FeaturedItemData, FeaturedApiConfig } from './FeaturedService';
import { fetchFeaturedItems, DEFAULT_FEATURED_CONFIG } from './FeaturedService';
import { logDebug, logError } from '../../../utils/logger';

export interface UseFeaturedItemsOptions {
  /** Whether to fetch data dynamically (default: true) */
  fetchDynamic?: boolean;
  /** Voyager API base URL */
  voyagerBaseUrl?: string;
  /** Voyager config ID */
  voyagerConfigId?: string;
  /** Filter query for featured items (e.g., tag_tags:featured) */
  featuredQuery?: string;
  /** Maximum number of items to fetch */
  maxItems?: number;
  /** Static items to use when not fetching dynamically */
  staticItems?: FeaturedItemData[];
  /** Base URL for item details page */
  detailsPageBaseUrl?: string;
}

export interface UseFeaturedItemsResult {
  /** The featured items data */
  items: FeaturedItemData[];
  /** Loading state */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Function to manually refresh the data */
  refresh: () => void;
}

/**
 * Hook to fetch and manage featured items data
 */
export function useFeaturedItems(
  options: UseFeaturedItemsOptions = {},
): UseFeaturedItemsResult {
  const {
    fetchDynamic = true,
    voyagerBaseUrl = DEFAULT_FEATURED_CONFIG.baseUrl,
    voyagerConfigId = DEFAULT_FEATURED_CONFIG.configId,
    featuredQuery = DEFAULT_FEATURED_CONFIG.featuredQuery,
    maxItems = 48,
    staticItems = [],
    detailsPageBaseUrl = DEFAULT_FEATURED_CONFIG.detailsPageBaseUrl,
  } = options;

  const [items, setItems] = useState<FeaturedItemData[]>(staticItems);
  const [isLoading, setIsLoading] = useState(fetchDynamic);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!fetchDynamic) {
      logDebug('[useFeaturedItems] Using static items');
      setItems(staticItems);
      setIsLoading(false);
      return;
    }

    logDebug('[useFeaturedItems] Starting dynamic fetch');
    setIsLoading(true);
    setError(null);

    try {
      const config: FeaturedApiConfig = {
        baseUrl: voyagerBaseUrl,
        configId: voyagerConfigId,
        rows: maxItems,
        featuredQuery: featuredQuery,
        detailsPageBaseUrl: detailsPageBaseUrl,
      };

      const fetchedItems = await fetchFeaturedItems(config);
      logDebug('[useFeaturedItems] Fetched', fetchedItems.length, 'items');
      logDebug('[useFeaturedItems] Sample item:', fetchedItems[0]);
      setItems(fetchedItems);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch featured items';
      logError('[useFeaturedItems] Fetch error:', errorMessage);
      setError(errorMessage);
      // Fall back to static items on error
      if (staticItems.length > 0) {
        setItems(staticItems);
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    fetchDynamic,
    voyagerBaseUrl,
    voyagerConfigId,
    featuredQuery,
    maxItems,
    staticItems,
    detailsPageBaseUrl,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    items,
    isLoading,
    error,
    refresh,
  };
}

export default useFeaturedItems;
